/**
 * Webchat public endpoints — fetch wrappers for the visitor widget.
 *
 * We intentionally do NOT use the shared `api` axios instance because that
 * one attaches the operator's Cognito Authorization header. The widget runs
 * on the public santinho and uses its own per-visitor JWT, so it talks to
 * the BASE_URL (without the /api prefix) like other public endpoints.
 */

import { BASE_URL } from './api';

export interface WebchatCreateSessionBody {
  name: string;
  phone: string;
  email?: string;
  consent: true;
  consentVersion?: string;
  turnstileToken?: string;
  visitorId?: string;
}

export interface WebchatSessionResponse {
  visitorId: string;
  conversationId: string;
  token: string;
  expiresAt: string;
}

export interface WebchatMessageItem {
  id: string;
  direction: 'inbound' | 'outbound';
  senderName?: string;
  text?: string;
  createdAt: string;
}

async function request<T>(
  path: string,
  init: RequestInit & { authToken?: string; tenantSlug?: string }
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (init.authToken) headers.set('Authorization', `Bearer ${init.authToken}`);
  if (init.tenantSlug) headers.set('X-Tenant-Slug', init.tenantSlug);

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
    cache: 'no-store',
  });

  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const err = (data as { error?: { message?: string } } | null)?.error;
    const message = err?.message || `Request failed (${response.status})`;
    const e = new Error(message) as Error & { status?: number };
    e.status = response.status;
    throw e;
  }

  return data as T;
}

export const webchatService = {
  async createSession(
    tenantSlug: string,
    body: WebchatCreateSessionBody
  ): Promise<WebchatSessionResponse> {
    return request<WebchatSessionResponse>('/public/webchat/session', {
      method: 'POST',
      body: JSON.stringify(body),
      tenantSlug,
    });
  },

  async sendMessage(token: string, text: string): Promise<{ messageId: string; conversationId: string }> {
    return request('/public/webchat/messages', {
      method: 'POST',
      body: JSON.stringify({ text }),
      authToken: token,
    });
  },

  async listMessages(
    token: string,
    since?: string
  ): Promise<{ messages: WebchatMessageItem[] }> {
    const qs = since ? `?since=${encodeURIComponent(since)}` : '';
    return request(`/public/webchat/messages${qs}`, {
      method: 'GET',
      authToken: token,
    });
  },

  /**
   * Visitor-side "encerrar conversa". Closes the conversation server-side
   * (status=closed), finalizes the session, and removes the visitor<->contact
   * mapping so the next widget open starts fresh.
   */
  async endSession(token: string): Promise<{ ok: boolean }> {
    return request('/public/webchat/session/close', {
      method: 'POST',
      authToken: token,
    });
  },
};
